import cx from 'classnames'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import {
  FontFamilyStyle,
  FontSizeStyle,
  FontWeightStyle,
  ItalicStyle,
  LetterSpacingStyle,
  LineHeightStyle,
  StrikethroughStyle,
  UnderlineStyle,
  UppercaseStyle,
  VerticalAlignStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { DefaultHorizontalAlignStyle, track } from '@tldraw/tldraw'
import { useRelevantStyles } from 'components/VisualStudioPlayground/VisualEditor/hooks/useRelevantStyles'
import Styles from '../ShapesTab.module.scss'
import { FontFamilyWithUploadedFonts } from 'components/VisualStudioPlayground/FontFamily/FontFamily'
import { Text } from 'blaze-ui/Text/Text'
import { FontWeight } from 'components/VisualStudioPlayground/FontWeight/FontWeight'
import { FontSize } from 'components/VisualStudioPlayground/FontSize/FontSize'
import { Button } from 'blaze-ui/Button'
import {
  AlignCenter,
  AlignDown2,
  AlignLeft,
  AlignRight,
  AlignUp2,
  ChevronDown,
  Eraser,
  LineHeight,
  Strikethrough,
  Underline,
  UpperCase,
} from 'blaze-ui/icons'
import { useUpdateStyle } from 'components/VisualStudioPlayground/VisualEditor/hooks/useUpdateStyle'
import {
  clearProseMarks,
  richTextHasMarks,
  stripRichTextMarks,
  toggleProseMark,
  useActiveProseMarks,
} from 'components/VisualStudioPlayground/Shapes/TextBoxShape/activeProseView'
import { TextBoxShape } from 'components/VisualStudioPlayground/Shapes/TextBoxShape/Types'
import { StyleToggleButton } from 'components/VisualStudioPlayground/Toolbar/StyleToggleButton'
import { Bold } from 'components/VisualStudioPlayground/Bold/Bold'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { SliderField } from 'components/Design/SliderField/SliderField'
import { useRootStore } from 'stores/RootStore'
import { useBrandVisualKits } from 'hooks/queries/workspaces/useBrandVisualKits'
import { AlignHorizontalCenter1 } from 'blaze-ui/icons/24'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Italic } from 'components/VisualStudioPlayground/Italic/Italic'

const FontToolsCard = track(() => {
  const editor = useEditor()
  const relevantStyles = useRelevantStyles(editor)
  const updateStyle = useUpdateStyle(editor)
  const { designStore } = useRootStore()
  const canAccessAdminTemplates = useFlipperFlag('admin_templates')
  const proseMarks = useActiveProseMarks()

  if (!editor || !relevantStyles) return null

  const selectedShape = editor.getOnlySelectedShape()
  const textBox = selectedShape?.type === 'textBox' ? (selectedShape as TextBoxShape) : null

  // Live-editor marks win while editing; otherwise read the shape's stored richText.
  const hasInlineMarks = proseMarks ? proseMarks.hasMarks : richTextHasMarks(textBox?.props.richText)

  const clearMarks = () => {
    if (clearProseMarks()) return // edit mode: cleared via the live editor
    if (textBox?.props.richText) {
      editor.updateShape({
        id: textBox.id,
        type: textBox.type,
        props: { richText: stripRichTextMarks(textBox.props.richText) },
      })
    }
  }

  const { styles } = relevantStyles

  const fontSize = styles.get(FontSizeStyle)
  const fontFamily = styles.get(FontFamilyStyle)
  const fontWeight = styles.get(FontWeightStyle)
  const italic = styles.get(ItalicStyle)
  const underline = styles.get(UnderlineStyle)
  const strikethrough = styles.get(StrikethroughStyle)
  const uppercase = styles.get(UppercaseStyle)
  const align = styles.get(DefaultHorizontalAlignStyle)
  const lineHeight = styles.get(LineHeightStyle)
  const letterSpacing = styles.get(LetterSpacingStyle)
  const verticalAlign = styles.get(VerticalAlignStyle)

  const { data } = useBrandVisualKits()

  const brandFontFamilies = data?.brand_visual_kits
    ?.find((brandVisualKit) => brandVisualKit.id === designStore?.brandVisualKitId)
    ?.text_styles.map(({ style }) => style.font_family)
    .filter(Boolean) as string[]

  return (
    <div className={cx(Styles.card, Styles.textCard)}>
      {fontFamily && fontWeight && fontSize && italic && (
        <>
          <div className={Styles.textSection}>
            <Text variant="smallList">Font Style</Text>
            <FontFamilyWithUploadedFonts
              editor={editor}
              style={fontFamily}
              fontWeightStyle={fontWeight}
              italicStyle={italic}
              brandKitFontFamilies={brandFontFamilies}
            />
          </div>
          <div className={Styles.columns}>
            <div className={Styles.textSection}>
              <Text variant="smallList">Size</Text>
              <FontSize editor={editor} style={fontSize} />
            </div>
            <div className={Styles.textSection}>
              <Text variant="smallList">Weight</Text>
              <FontWeight editor={editor} style={fontWeight} fontFamilyStyle={fontFamily} italicStyle={italic} />
            </div>
          </div>
        </>
      )}
      {align && (
        <div className={Styles.textSection}>
          <Text variant="smallList">Alignment</Text>
          <div className={Styles.align}>
            {align.type === 'shared' && (
              <>
                <Button
                  className={align.value === 'start' ? Styles.buttonOn : ''}
                  aria-pressed={align.value === 'start'}
                  frontIcon={AlignLeft}
                  aria-label="Align text left"
                  variant="tertiary"
                  onPress={() => updateStyle(DefaultHorizontalAlignStyle, 'start')}
                >
                  Left
                </Button>
                <Button
                  className={align.value === 'middle' ? Styles.buttonOn : ''}
                  aria-pressed={align.value === 'middle'}
                  frontIcon={AlignCenter}
                  aria-label="Align text center"
                  variant="tertiary"
                  onPress={() => updateStyle(DefaultHorizontalAlignStyle, 'middle')}
                >
                  Center
                </Button>
                <Button
                  className={align.value === 'end' ? Styles.buttonOn : ''}
                  aria-pressed={align.value === 'end'}
                  frontIcon={AlignRight}
                  aria-label="Align text right"
                  variant="tertiary"
                  onPress={() => updateStyle(DefaultHorizontalAlignStyle, 'end')}
                >
                  Right
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {verticalAlign && canAccessAdminTemplates && (
        <div className={Styles.textSection}>
          <Text variant="smallList">Vertical Alignment</Text>
          <div className={Styles.align}>
            {verticalAlign.type === 'shared' && (
              <>
                <Button
                  frontIcon={AlignUp2}
                  aria-label="Align text top"
                  variant="tertiary"
                  className={verticalAlign.value === 'top' ? Styles.buttonOn : ''}
                  aria-pressed={verticalAlign.value === 'top'}
                  onPress={() => updateStyle(VerticalAlignStyle, 'top')}
                >
                  Top
                </Button>
                <Button
                  frontIcon={AlignHorizontalCenter1}
                  aria-label="Align text middle"
                  variant="tertiary"
                  className={verticalAlign.value === 'middle' ? Styles.buttonOn : ''}
                  aria-pressed={verticalAlign.value === 'middle'}
                  onPress={() => updateStyle(VerticalAlignStyle, 'middle')}
                >
                  Middle
                </Button>
                <Button
                  frontIcon={AlignDown2}
                  aria-label="Align text bottom"
                  variant="tertiary"
                  className={verticalAlign.value === 'bottom' ? Styles.buttonOn : ''}
                  aria-pressed={verticalAlign.value === 'bottom'}
                  onPress={() => updateStyle(VerticalAlignStyle, 'bottom')}
                >
                  Bottom
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      <div className={Styles.alignmentButtons}>
        {fontFamily && fontWeight && fontSize && italic && (
          <Bold
            editor={editor}
            fontWeightStyle={fontWeight}
            fontFamilyStyle={fontFamily}
            italicStyle={italic}
            size="md"
            tooltipPlacement="top"
          />
        )}
        {fontFamily && fontWeight && italic && (
          <Italic
            editor={editor}
            fontWeightStyle={fontWeight}
            fontFamilyStyle={fontFamily}
            italicStyle={italic}
            tooltipPlacement="top"
          />
        )}
        {strikethrough && (
          <Tooltip label="Strikethrough" placement="top">
            <span>
              <StyleToggleButton
                style={proseMarks ? { type: 'shared', value: proseMarks.strike === 'all' } : strikethrough}
                onPress={(value) => {
                  if (!toggleProseMark('strike')) updateStyle(StrikethroughStyle, value)
                }}
                icon={Strikethrough}
                aria-label="Strikethrough"
              />
            </span>
          </Tooltip>
        )}
        {underline && (
          <Tooltip label="Underline" placement="top">
            <span>
              <StyleToggleButton
                style={proseMarks ? { type: 'shared', value: proseMarks.underline === 'all' } : underline}
                onPress={(value) => {
                  if (!toggleProseMark('underline')) updateStyle(UnderlineStyle, value)
                }}
                icon={Underline}
                aria-label="Underline"
              />
            </span>
          </Tooltip>
        )}
        {uppercase && (
          <Tooltip label="Uppercase" placement="top">
            <span>
              <StyleToggleButton
                style={uppercase}
                onPress={(value) => updateStyle(UppercaseStyle, value)}
                icon={UpperCase}
                aria-label="Uppercase"
              />
            </span>
          </Tooltip>
        )}
        {lineHeight && letterSpacing && (
          <DropdownTrigger placement="bottom-start">
            <Tooltip label="Spacing" placement="top">
              <Button variant="tertiary" frontIcon={LineHeight} aria-label="Spacing" useChildClass>
                <span className={Styles.chevron}>
                  <ChevronDown size={14} />
                </span>
              </Button>
            </Tooltip>
            {() => (
              <div className={Styles.popover}>
                <SliderField
                  label="Letter spacing"
                  step={1}
                  minValue={-200}
                  maxValue={800}
                  pinnedValue={0}
                  value={letterSpacing.type === 'shared' ? letterSpacing.value : 0}
                  onChange={(value) => updateStyle(LetterSpacingStyle, value)}
                />
                <SliderField
                  label="Line height"
                  step={0.01}
                  minValue={0.5}
                  maxValue={2.5}
                  pinnedValue={1.4}
                  value={lineHeight.type === 'shared' ? lineHeight.value : 0}
                  onChange={(value) => updateStyle(LineHeightStyle, value)}
                />
              </div>
            )}
          </DropdownTrigger>
        )}
        {hasInlineMarks && (
          <Tooltip label="Clear formatting" placement="top">
            <span>
              <Button variant="tertiary" frontIcon={Eraser} aria-label="Clear formatting" onPress={clearMarks} />
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  )
})

export { FontToolsCard }
